/**
 * Sticky Add to Cart Bar for Mobile
 * Shows a fixed bar at the bottom of the screen when user scrolls past the original Add to Cart button
 */

class StickyAtcBar {
  constructor() {
    this.stickyBar = document.querySelector('.sticky-atc-bar');
    if (!this.stickyBar) return;

    this.sectionId = this.stickyBar.dataset.sectionId;
    this.originalButtons = document.querySelector(`#ProductInfo-${this.sectionId} .product-form__buttons`);
    this.stickyButton = this.stickyBar.querySelector('.sticky-atc-button');
    this.stickyQuantityInput = this.stickyBar.querySelector('.quantity__input');
    this.originalQuantityInput = document.querySelector(`#Quantity-${this.sectionId}`);
    this.originalForm = document.querySelector(`#product-form-${this.sectionId}`);

    if (!this.originalButtons) return;

    this.isVisible = false;
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;

    this.init();
  }

  init() {
    this.setupScrollObserver();
    this.setupResizeListener();
    this.setupButtonClick();
    this.setupQuantitySync();
    this.setupVariantChangeListener();
  }

  setupScrollObserver() {
    // Use Intersection Observer to detect when original buttons scroll out of view
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (this.isMobile) {
            if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
              this.show();
            } else {
              this.hide();
            }
          }
        });
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px 0px 0px 0px',
      }
    );

    this.observer.observe(this.originalButtons);
  }

  setupResizeListener() {
    const mediaQuery = window.matchMedia('(max-width: 749px)');

    const handleResize = (e) => {
      this.isMobile = e.matches;
      if (!this.isMobile) {
        this.hide();
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleResize);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleResize);
    }
  }

  show() {
    if (this.isVisible) return;
    this.isVisible = true;
    this.stickyBar.classList.add('is-visible');
    document.body.classList.add('sticky-atc-active');
  }

  hide() {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.stickyBar.classList.remove('is-visible');
    document.body.classList.remove('sticky-atc-active');
  }

  setupButtonClick() {
    if (!this.stickyButton || !this.originalForm) return;

    this.stickyButton.addEventListener('click', (e) => {
      e.preventDefault();

      // Sync quantity before submitting
      this.syncQuantityToOriginal();

      // Trigger the original form submission
      const submitButton = this.originalForm.querySelector('[type="submit"]');
      if (submitButton && !submitButton.disabled) {
        // Show loading state on sticky button
        this.stickyButton.classList.add('loading');
        this.stickyButton.setAttribute('aria-disabled', 'true');

        // Click the original submit button
        submitButton.click();

        // Reset loading state after a delay (the cart drawer/notification will handle UI)
        setTimeout(() => {
          this.stickyButton.classList.remove('loading');
          this.stickyButton.removeAttribute('aria-disabled');
        }, 1500);
      }
    });
  }

  setupQuantitySync() {
    if (!this.stickyQuantityInput || !this.originalQuantityInput) return;

    // Sync sticky quantity to original when sticky changes
    this.stickyQuantityInput.addEventListener('change', () => {
      this.syncQuantityToOriginal();
    });

    // Sync original quantity to sticky when original changes
    this.originalQuantityInput.addEventListener('change', () => {
      this.syncQuantityFromOriginal();
    });

    // Also listen for quantity button clicks in sticky bar
    const stickyQuantityButtons = this.stickyBar.querySelectorAll('.quantity__button');
    stickyQuantityButtons.forEach((button) => {
      button.addEventListener('click', () => {
        // Small delay to let the quantity-input component update the value
        setTimeout(() => {
          this.syncQuantityToOriginal();
        }, 50);
      });
    });

    // Listen for quantity button clicks in original
    const originalQuantityButtons = document.querySelectorAll(
      `#ProductInfo-${this.sectionId} .quantity__button`
    );
    originalQuantityButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setTimeout(() => {
          this.syncQuantityFromOriginal();
        }, 50);
      });
    });
  }

  syncQuantityToOriginal() {
    if (this.originalQuantityInput && this.stickyQuantityInput) {
      this.originalQuantityInput.value = this.stickyQuantityInput.value;
      // Dispatch change event to trigger any listeners on the original input
      this.originalQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  syncQuantityFromOriginal() {
    if (this.originalQuantityInput && this.stickyQuantityInput) {
      this.stickyQuantityInput.value = this.originalQuantityInput.value;
    }
  }

  setupVariantChangeListener() {
    // Listen for variant changes to update button state and price
    document.addEventListener('variant:change', () => {
      this.updateButtonState();
      this.updatePrice();
    });

    // Also use a MutationObserver to watch for changes to the original submit button
    const originalSubmitButton = this.originalForm?.querySelector('[type="submit"]');
    if (originalSubmitButton) {
      const observer = new MutationObserver(() => {
        this.updateButtonState();
      });

      observer.observe(originalSubmitButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
      });
    }

    // Watch for price updates in the original price container
    const originalPriceContainer = document.querySelector(`#price-${this.sectionId}`);
    if (originalPriceContainer) {
      const priceObserver = new MutationObserver(() => {
        this.updatePrice();
      });

      priceObserver.observe(originalPriceContainer, {
        childList: true,
        subtree: true,
      });
    }
  }

  updateButtonState() {
    const originalSubmitButton = this.originalForm?.querySelector('[type="submit"]');
    if (!originalSubmitButton || !this.stickyButton) return;

    // Copy disabled state
    if (originalSubmitButton.disabled || originalSubmitButton.getAttribute('aria-disabled') === 'true') {
      this.stickyButton.disabled = true;
    } else {
      this.stickyButton.disabled = false;
    }

    // Copy button text
    const originalButtonText = originalSubmitButton.querySelector('span:not(.loading__spinner)');
    const stickyButtonText = this.stickyButton.querySelector('span:not(.loading__spinner)');
    if (originalButtonText && stickyButtonText) {
      stickyButtonText.textContent = originalButtonText.textContent;
    }
  }

  updatePrice() {
    const originalPriceContainer = document.querySelector(`#price-${this.sectionId}`);
    const stickyPriceContainer = document.querySelector(`#StickyAtcPrice-${this.sectionId}`);

    if (originalPriceContainer && stickyPriceContainer) {
      // Clone the price HTML from original
      const priceElement = originalPriceContainer.querySelector('.price');
      if (priceElement) {
        const clonedPrice = priceElement.cloneNode(true);
        // Remove badges from sticky price
        const badges = clonedPrice.querySelectorAll('.badge');
        badges.forEach((badge) => badge.remove());

        stickyPriceContainer.innerHTML = '';
        stickyPriceContainer.appendChild(clonedPrice);
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new StickyAtcBar());
} else {
  new StickyAtcBar();
}
